import { motion } from 'framer-motion';
import { useMemo } from 'react';
import Card3D from './battle/Card3D';
import WarTable from './battle/WarTable';

const KINGDOM_ACCENT = {
  Italy: '#3ddc84', Spain: '#ffc400', France: '#8ab0ff',
  England: '#ff8f9b', Unknown: '#ffd700',
};

// ── Carved roster plaque: which numbers are still on the field ──
const BattleRoster = ({ cardInfo, mobile, accent }) => (
  <motion.div
    initial={{ opacity: 0, y: -14 }} animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6 }}
    data-testid="cards-on-table-box"
    style={{
      position: 'relative', overflow: 'hidden',
      display: 'inline-block', margin: '0 auto',
      borderRadius: mobile ? 8 : 12,
      padding: mobile ? '5px 12px 6px' : '9px 26px 11px',
      marginBottom: mobile ? 8 : 14,
      background: 'linear-gradient(165deg, rgba(26,20,10,0.96) 0%, rgba(8,5,2,0.98) 100%)',
      boxShadow: `0 0 0 1px rgba(255,215,0,0.38), 0 6px 20px rgba(0,0,0,0.8), inset 0 1px 0 rgba(255,225,160,0.12), inset 0 -8px 18px rgba(0,0,0,0.6)`,
    }}
  >
    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '45%', background: 'linear-gradient(to bottom, rgba(255,235,180,0.06), transparent)', pointerEvents: 'none' }} />
    <div style={{
      fontSize: mobile ? 6.5 : 8.5, fontWeight: 800, letterSpacing: '0.24em',
      color: 'rgba(255,215,0,0.45)', textTransform: 'uppercase', textAlign: 'center',
      marginBottom: mobile ? 2 : 4,
    }}>
      ⚔ Ordine di Battaglia ⚔
    </div>
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: mobile ? 10 : 20, flexWrap: 'wrap' }}>
      {cardInfo.map((c, i) => (
        <motion.span key={i}
          animate={c.revealed ? { opacity: 0.4, scale: 0.88 } : { opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
          data-testid={`card-value-${i}`}
          style={{
            fontFamily: "'Cinzel',serif",
            fontSize: mobile ? 15 : 24, fontWeight: 900,
            color: c.revealed ? 'rgba(110,95,58,0.55)' : accent,
            textDecorationLine: c.revealed ? 'line-through' : 'none',
            textDecorationColor: 'rgba(160,40,30,0.75)',
            textDecorationThickness: '2px',
            textShadow: c.revealed ? 'none' : `0 2px 0 rgba(90,55,0,1), 0 3px 6px rgba(0,0,0,0.95), 0 0 14px ${accent}55`,
          }}
        >
          {c.displayValue ?? c.value}
        </motion.span>
      ))}
    </div>
  </motion.div>
);

const CardGrid = ({ cards, onPickCard, canPick, revealingCardIndex = null, mobile = false, kingdom = 'Unknown' }) => {
  // Hidden values stay shuffled so nobody can read the board
  const cardInfo = useMemo(() => {
    const mapped = cards.map((c, i) => ({ value: c.value, revealed: c.revealed || revealingCardIndex === i }));
    const unrevealed = mapped.filter(c => !c.revealed).map(c => c.value);
    for (let i = unrevealed.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [unrevealed[i], unrevealed[j]] = [unrevealed[j], unrevealed[i]];
    }
    let ui = 0;
    return mapped.map(c => c.revealed ? c : { ...c, displayValue: unrevealed[ui++] });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cards.map(c => c.value + ':' + c.revealed).join(','), revealingCardIndex]);

  const accent = KINGDOM_ACCENT[kingdom] || KINGDOM_ACCENT.Unknown;

  return (
    <div style={{ width: '100%', maxWidth: mobile ? 260 : 820, margin: '0 auto', padding: mobile ? '0 2px' : '0 8px' }}>
      <div style={{ textAlign: 'center' }}>
        <BattleRoster cardInfo={cardInfo} mobile={mobile} accent={accent} />
      </div>

      <WarTable kingdom={kingdom} mobile={mobile} active={canPick}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: mobile ? 'repeat(2, auto)' : 'repeat(4, auto)',
          rowGap: mobile ? 16 : 26,
          columnGap: mobile ? 12 : 22,
          justifyContent: 'center', justifyItems: 'center',
          transformStyle: 'preserve-3d',
        }}>
          {cards.map((card, index) => {
            const isRevealing = revealingCardIndex === index;
            const cardToRender = isRevealing ? { ...card, revealed: true } : card;
            const isLocked = card.revealed === true || isRevealing;
            return (
              <motion.div key={index}
                initial={{ opacity: 0, y: -70, rotateZ: index % 2 ? 9 : -9 }}
                animate={{ opacity: 1, y: 0, rotateZ: 0 }}
                transition={{ delay: 0.25 + index * 0.11, type: 'spring', stiffness: 150, damping: 15 }}
                style={{ transformStyle: 'preserve-3d' }}
              >
                <Card3D
                  card={cardToRender}
                  index={index}
                  onPick={onPickCard}
                  canPick={canPick && !isLocked}
                  isRevealing={isRevealing}
                  mobile={mobile}
                  kingdom={kingdom}
                />
              </motion.div>
            );
          })}
        </div>
      </WarTable>
    </div>
  );
};

export default CardGrid;
